import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Globe, Users, ShieldCheck, Calendar, ExternalLink, Loader2, BarChart3, Award, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import API from '@/lib/api';

export function AgencyProfileBody({ agency, loading, compact = false }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8" data-testid="agency-profile-loading">
        <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }
  if (!agency) {
    return <p className="text-sm text-slate-500 text-center py-6">Agency profile unavailable.</p>;
  }
  const stats = agency.stats || {};
  const joined = agency.joined_at ? new Date(agency.joined_at) : null;
  const joinedLabel = joined ? joined.toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) : null;
  const verified = agency.verified_status === 'verified';
  const logo = agency.logo_url
    ? (agency.logo_url.startsWith('http') ? agency.logo_url : `${process.env.REACT_APP_BACKEND_URL}${agency.logo_url}`)
    : '';

  return (
    <div data-testid="agency-profile-body">
      {/* Hero */}
      <div className="flex items-start gap-4 mb-5">
        <div className="w-16 h-16 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
          {logo ? <img src={logo} alt="logo" className="w-full h-full object-cover" /> : <Building2 className="w-7 h-7 text-slate-400" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-semibold text-slate-900">{agency.name}</h3>
            {verified && (
              <Badge className="bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-50 text-[10px]">
                <ShieldCheck className="w-3 h-3 mr-0.5" /> Verified
              </Badge>
            )}
          </div>
          {agency.tagline && <p className="text-xs text-slate-500 mt-0.5">{agency.tagline}</p>}
          {joinedLabel && (
            <p className="text-[10px] text-slate-400 mt-1.5 flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Joined {joinedLabel}
            </p>
          )}
        </div>
      </div>

      {/* About */}
      {agency.about && (
        <p className="text-xs text-slate-700 leading-relaxed mb-4 whitespace-pre-line">{agency.about}</p>
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <StatCell icon={FileText} value={stats.campaigns_count ?? 0} label="Campaigns" />
        <StatCell icon={Users} value={stats.creators_worked_with ?? 0} label="Creators" />
        <StatCell icon={Award} value={stats.acceptance_rate != null ? `${stats.acceptance_rate}%` : '—'} label="Accept rate" />
      </div>

      {/* Details */}
      <div className="space-y-2 text-xs text-slate-600">
        {agency.team_size && (
          <Detail icon={Users} label="Team size">{agency.team_size} people</Detail>
        )}
        {agency.website && (
          <Detail icon={Globe} label="Website">
            <a href={agency.website.startsWith('http') ? agency.website : `https://${agency.website}`}
               target="_blank" rel="noreferrer"
               className="text-blue-600 hover:underline inline-flex items-center gap-1">
              {agency.website.replace(/^https?:\/\//, '')}
              <ExternalLink className="w-3 h-3" />
            </a>
          </Detail>
        )}
        {agency.default_governing_law && (
          <Detail icon={ShieldCheck} label="Governing law">{agency.default_governing_law}</Detail>
        )}
      </div>

      {compact && stats.proposals_sent > 0 && (
        <p className="text-[10px] text-slate-400 mt-4 text-center">
          {stats.proposals_sent} proposal{stats.proposals_sent === 1 ? '' : 's'} sent on Mylikeness
        </p>
      )}
    </div>
  );
}

function StatCell({ icon: Icon, value, label }) {
  return (
    <div className="bg-slate-50 rounded-xl border border-slate-100 p-3 text-center">
      <Icon className="w-3.5 h-3.5 text-slate-400 mx-auto mb-1" />
      <p className="text-base font-semibold text-slate-900 leading-none">{value}</p>
      <p className="text-[10px] text-slate-500 mt-1">{label}</p>
    </div>
  );
}

function Detail({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
      <div>
        <span className="text-[10px] text-slate-500 uppercase tracking-wide font-semibold">{label}: </span>
        <span className="text-slate-700">{children}</span>
      </div>
    </div>
  );
}

export default function AgencyProfileModal({ open, onClose, agencyId, fallbackAgency }) {
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open || !agencyId) return;
    setLoading(true);
    API.get(`/public/agencies/${agencyId}`)
      .then(r => setAgency(r.data))
      .catch(() => setAgency(fallbackAgency || null))
      .finally(() => setLoading(false));
  }, [open, agencyId, fallbackAgency]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="agency-profile-modal">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="text-sm font-medium text-slate-500">Agency profile</DialogTitle>
        </DialogHeader>
        <div className="px-6 pb-5">
          <AgencyProfileBody agency={agency} loading={loading} compact />
        </div>
        <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 flex justify-between">
          <Button variant="outline" onClick={onClose} className="h-8 text-xs"
            data-testid="agency-modal-close">
            Close
          </Button>
          <Button
            onClick={() => { onClose(); navigate(`/creator/agencies/${agencyId}`); }}
            className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="agency-modal-view-full"
          >
            View full profile <ExternalLink className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
